import React from 'react';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../assets/styles/yearDisplay.css'
import AttendanceBarPlot from './AttendanceBarPlot';
import { useAuth } from './Auth/AuthContext';
import SessionDisplay from './sessiondisplay';
import { API_BASE_URL } from '../config/api';

const YearDisplay = () => {
    const location = useLocation();
    const { year } = useParams();
    const navigate = useNavigate();
    const [showJanuary, setShowJanuary] = useState(false);
    const [showFebruary, setShowFebruary] = useState(false);
    const [showMarch, setShowMarch] = useState(false);
    const [showApril, setShowApril] = useState(false);
    const [showMay, setShowMay] = useState(false);
    const [showJune, setShowJune] = useState(false);
    const [showJuly, setShowJuly] = useState(false);
    const [showAugust, setShowAugust] = useState(false);
    const [showSeptember, setShowSeptember] = useState(false);
    const [showOctober, setShowOctober] = useState(false);
    const [showNovember, setShowNovember] = useState(false);
    const [showDecember, setShowDecember] = useState(false);
    const { token } = useAuth();
    const [totalSessions, setTotalSessions] = useState(0);
    const [ytdTotal, setYtdTotal] = useState(0);
    const [bestSession, setBestSession] = useState({ attendees: 0, date: '—' });
    const [avgAttendance, setAvgAttendance] = useState(0);

    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    const MONTHS = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const showStates = {
        January: showJanuary, February: showFebruary, March: showMarch,
        April: showApril, May: showMay, June: showJune,
        July: showJuly, August: showAugust, September: showSeptember,
        October: showOctober, November: showNovember, December: showDecember
    };

    const setShowStates = {
        January: setShowJanuary, February: setShowFebruary, March: setShowMarch,
        April: setShowApril, May: setShowMay, June: setShowJune,
        July: setShowJuly, August: setShowAugust, September: setShowSeptember,
        October: setShowOctober, November: setShowNovember, December: setShowDecember
    };    

    const [monthSessions, setMonthSessions] = useState({
        January: [], February: [], March: [], April: [],
        May: [], June: [], July: [], August: [],
        September: [], October: [], November: [], December: []
    });

    useEffect(() => {
        const fetchYearSessions = async() => {
            try {
                const res = await fetch(`${API_BASE_URL}/attendance/sessions/year/${year}/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await res.json();

                const monthList = [
                    "January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"
                ];

                const groupedList = {
                    January: [], February: [], March: [], April: [],
                    May: [], June: [], July: [], August: [],
                    September: [], October: [], November: [], December: []
                };

                for (const session of data) {
                    const [sessionYear, sessionMonth, sessionDay] = session.Date.split('-');
                    const monthName = monthList[parseInt(sessionMonth) - 1];
                    
                    groupedList[monthName].push({
                        date: session.Date,
                        day: parseInt(sessionDay),
                        year: parseInt(sessionYear),
                        attendees: session.attendeeCount
                    });
                };

                setMonthSessions(groupedList);

                const allSessions = Object.values(groupedList).flat();

                const totalSessions = allSessions.length;
                const ytdTotal = allSessions.reduce((sum, s) => sum + s.attendees, 0);
                const bestSession = allSessions.reduce((best, s) => s.attendees > best.attendees ? s : best, { attendees: 0, date: '—' });
                const avgAttendance = totalSessions > 0 ? Math.round(ytdTotal / totalSessions) : 0;

                setTotalSessions(totalSessions);
                setYtdTotal(ytdTotal);
                setBestSession(bestSession);
                setAvgAttendance(avgAttendance);

            } catch(err) {
                console.log('Error fetching all the years: ', err);
            }
        };

        if(year) fetchYearSessions();
    }, [year]);

    const handleGenerateSpreadsheet = async () => {
    try {
        setIsGeneratingReport(true);

        const response = await fetch(
            `${API_BASE_URL}/attendance/sessions/year/${year}/report`,
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );

        if (!response.ok) {
            const message = await response.text();

            throw new Error(
                message ||
                `Spreadsheet generation failed with status ${response.status}`
            );
        }

        // XLSX files are binary, so use blob()
        const blob = await response.blob();

        // Create a temporary URL for the downloaded file
        const downloadUrl = window.URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');

        downloadLink.href = downloadUrl;
        downloadLink.download = `UUMC_Attendance_${year}.xlsx`;

        document.body.appendChild(downloadLink);

        downloadLink.click();

        downloadLink.remove();

        window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
        console.error(
            'Error generating spreadsheet:',
            err
        );

        alert(
            'Unable to generate the attendance spreadsheet.'
        );

    } finally {
        setIsGeneratingReport(false);
    }
};


    const handleMonthClick = (month) => {
        navigate(`/filter-by-month/${year}/${month}`);
    }

    return (
        <div className='container'>
            <div className='row align-items-center mb-3'>
                <div className='col'>
                    <h1 className="uumc-year-title">
                        Filter by year: {year}
                    </h1>
                </div>

                <div className='col-auto'>
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleGenerateSpreadsheet}
                        disabled={isGeneratingReport}
                    >
                        {isGeneratingReport
                            ? 'Generating...'
                            : 'Generate Spreadsheet'}
                    </button>
                </div>
            </div>

            <div className="uumc-year-metrics">
                <div className="uumc-year-metric">
                    <div className="uumc-year-metric-label">Total sessions</div>
                    <div className="uumc-year-metric-value">{totalSessions}</div>
                </div>
                <div className="uumc-year-metric">
                    <div className="uumc-year-metric-label">Avg attendance</div>
                    <div className="uumc-year-metric-value">{avgAttendance}</div>
                    <div className="uumc-year-metric-sub">per session</div>
                </div>
                <div className="uumc-year-metric">
                    <div className="uumc-year-metric-label">Best session</div>
                    <div className="uumc-year-metric-value">{bestSession.attendees}</div>
                    <div className="uumc-year-metric-sub">{bestSession.date}</div>
                </div>
                <div className="uumc-year-metric">
                    <div className="uumc-year-metric-label">YTD total</div>
                    <div className="uumc-year-metric-value">{ytdTotal}</div>
                </div>
            </div>

            <div className="list-group row year-list" data-testid="year-filter-card">
                {MONTHS.map((month) => {
                    const isOpen = showStates[month];
                    const sessions = monthSessions[month] || [];

                    return (
                        <div key={month} className={`list-group-item uumc-month-row ${isOpen ? 'uumc-month-row--open' : ''}`} data-testid="month-card">
                            <div className="uumc-month-row-header" onClick={() => setShowStates[month](!isOpen)} data-testid="month-row-header">
                                <span className={`uumc-month-name ${isOpen ? 'uumc-month-name--open' : ''}`}>
                                    {month} {year}
                                </span>
                                <span className={`uumc-session-badge ${sessions.length > 0 ? 'uumc-session-badge--active' : ''} ${isOpen ? 'uumc-session-badge--open' : ''}`}>
                                    {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
                                    {sessions.length > 0 && (
                                        <span className="uumc-badge-chevron">{isOpen ? '▲' : '▼'}</span>
                                    )}
                                </span>
                            </div>
                            {isOpen && (
                                <div className="mt-2 ms-3 dropdown-plot" data-testid="month-session-dropdown">
                                    <div className='plot-wrapper'>
                                        {/* <AttendanceBarPlot month={month} year={year}/> */}
                                        <SessionDisplay month={month} year={year} monthSessions={monthSessions} embedded={true}/>
                                    </div>
                                    {/* <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick(month)}>
                                        View Attendance
                                    </button> */}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    )

    // return (
    //     <div className='container'>
    //         <div className='row'>
    //             <h1>Filter by year: {year}</h1>
    //         </div>
    //         <div class="list-group row year-list">
    //             {/* <div className="list-group-item">
    //                 <div onClick={() => setShowJanuary(!showJanuary)} style={{ cursor: 'pointer' }}>
    //                     January {year} ▼
    //                 </div>

    //                 <div className="uumc-month-header" onClick={() => setShowJanuary(!showJanuary)}>
    //                     <span>January {year}</span>
    //                     <span className={`uumc-month-chevron ${showJanuary ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showJanuary && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"January"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("January")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div> */}
    //             <div className={`list-group-item uumc-month-row ${showJanuary ? 'uumc-month-row--open' : ''}`}>
    //                 <div className="uumc-month-row-header" onClick={() => setShowJanuary(!showJanuary)}>
    //                     <span className={`uumc-month-name ${showJanuary ? 'uumc-month-name--open' : ''}`}>
    //                         January {year}
    //                     </span>
    //                     <span className={`uumc-session-badge ${(monthSessions.January?.length > 0) ? 'uumc-session-badge--active' : ''} ${showJanuary ? 'uumc-session-badge--open' : ''}`}>
    //                         {monthSessions.January?.length || 0} {monthSessions.January?.length === 1 ? 'session' : 'sessions'}
    //                         {monthSessions.January?.length > 0 && <span className="uumc-badge-chevron">{showJanuary ? '▲' : '▼'}</span>}
    //                     </span>
    //                 </div>
    //                 {showJanuary && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"January"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("January")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowFebruary(!showFebruary)} style={{ cursor: 'pointer' }}>
    //                     February {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => showFebruary(!showFebruary)}>
    //                     <span>February {year}</span>
    //                     <span className={`uumc-month-chevron ${showFebruary ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showFebruary && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"February"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("February")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowMarch(!showMarch)} style={{ cursor: 'pointer' }}>
    //                     March {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowMarch(!showMarch)}>
    //                     <span>March {year}</span>
    //                     <span className={`uumc-month-chevron ${showMarch ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showMarch && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"March"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("March")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowApril(!showApril)} style={{ cursor: 'pointer' }}>
    //                     April {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowApril(!showApril)}>
    //                     <span>April {year}</span>
    //                     <span className={`uumc-month-chevron ${showApril ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showApril && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"April"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("April")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowMay(!showMay)} style={{ cursor: 'pointer' }}>
    //                     May {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowMay(!showMay)}>
    //                     <span>May {year}</span>
    //                     <span className={`uumc-month-chevron ${showMay ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showMay && (
    //                     <div className="dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"May"} year={year} />
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => handleMonthClick("May")}>
    //                             View Attendance
    //                         </button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowJune(!showJune)} style={{ cursor: 'pointer' }}>
    //                     June {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowJune(!showJune)}>
    //                     <span>June {year}</span>
    //                     <span className={`uumc-month-chevron ${showJune ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showJune && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"June"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("June")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowJuly(!showJuly)} style={{ cursor: 'pointer' }}>
    //                     July {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowJuly(!showJuly)}>
    //                     <span>July {year}</span>
    //                     <span className={`uumc-month-chevron ${showJuly ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showJuly && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"July"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("July")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowAugust(!showAugust)} style={{ cursor: 'pointer' }}>
    //                     August {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowAugust(!showAugust)}>
    //                     <span>August {year}</span>
    //                     <span className={`uumc-month-chevron ${showAugust ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showAugust && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"August"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("August")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowSeptember(!showSeptember)} style={{ cursor: 'pointer' }}>
    //                     September {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowSeptember(!showSeptember)}>
    //                     <span>September {year}</span>
    //                     <span className={`uumc-month-chevron ${showSeptember ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showSeptember && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"September"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("September")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowOctober(!showOctober)} style={{ cursor: 'pointer' }}>
    //                     October {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowOctober(!showOctober)}>
    //                     <span>October {year}</span>
    //                     <span className={`uumc-month-chevron ${showOctober ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showOctober && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"October"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("October")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowNovember(!showNovember)} style={{ cursor: 'pointer' }}>
    //                     November {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowNovember(!showNovember)}>
    //                     <span>November {year}</span>
    //                     <span className={`uumc-month-chevron ${showNovember ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showNovember && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"November"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("November")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
    //             <div className="list-group-item">
    //                 {/* <div onClick={() => setShowDecember(!showDecember)} style={{ cursor: 'pointer' }}>
    //                     December {year} ▼
    //                 </div> */}

    //                 <div className="uumc-month-header" onClick={() => setShowDecember(!showDecember)}>
    //                     <span>December {year}</span>
    //                     <span className={`uumc-month-chevron ${showDecember ? 'open' : ''}`}>▼</span>
    //                 </div>

    //                 {showDecember && (
    //                     <div className="mt-2 ms-3 dropdown-plot">
    //                         <div className='plot-wrapper'>
    //                             <AttendanceBarPlot month={"December"} year={year}/>
    //                         </div>
    //                         <button className="btn btn-sm btn-outline-primary" onClick={() => handleMonthClick("December")}>View Attendance</button>
    //                     </div>
    //                 )}
    //             </div>
                
    //             {/* <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >January {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >February {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >March {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >April {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >May {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >June {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >July {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >August {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >September {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >October {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >November {year}</a>
    //             <a class="list-group-item list-group-item-action" onClick={handleMonthClick} >December {year}</a> */}
    //         </div>
    //     </div>
    // )
}

export default YearDisplay;